import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useFundTransferLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'fund-transfer',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_FUND_TRANSFER]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUND_TRANSFER) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_FUND_TRANSFER_US]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUND_TRANSFER_US) || EddYesNo.NO,
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_FUND_TRANSFER]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_FUND_TRANSFER_US]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data[EddEmbeddedFields.EDD_FUND_TRANSFER] === EddYesNo.YES) {
          const followUp = data[EddEmbeddedFields.EDD_FUND_TRANSFER_US];
          if (!followUp || followUp.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_FUND_TRANSFER_US],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const mainValue = formMethods.getValues(EddEmbeddedFields.EDD_FUND_TRANSFER);
    const followUpValue = formMethods.getValues(EddEmbeddedFields.EDD_FUND_TRANSFER_US);

    saveEddQuestionToLocalStore({
      question: t('organic.fundTransfer.title'),
      questionId: EddEmbeddedFields.EDD_FUND_TRANSFER,
      answer: JSON.stringify(mainValue),
    });

    if (mainValue === EddYesNo.YES) {
      saveEddQuestionToLocalStore({
        question: t('organic.fundTransfer.followUpTitle'),
        questionId: EddEmbeddedFields.EDD_FUND_TRANSFER_US,
        answer: JSON.stringify(followUpValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_FUND_TRANSFER);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddFundTransferSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
